
/**
 * Module dependencies.
 */

import { Text } from '../typography';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { typography } from '../typography/typography-styles';
import styled, { css } from 'styled-components';

/**
 * Export `FormGroup` styled component.
 */

export const FormGroup = styled.div<{ disabled: boolean; hasAlert?: boolean }>`
  margin-bottom: ${ifProp('hasAlert', '8px', '16px')};
  position: relative;

  ${ifProp('disabled', css`
    cursor: default;
    pointer-events: none;
  `)}

  * {
    outline: none;
  }
`;

/**
 * Export `FormControlWrapper` styled component.
 */

export const FormControlWrapper = styled.div<{ disabled: boolean; hasError: boolean; hasFocus: boolean }>`
  align-items: center;
  background-color: ${color('gray300')};
  border: 2px solid ${color('gray300')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  column-gap: 4px;
  display: flex;
  position: relative;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, border-color;

  ${ifProp('disabled', css`
    background-color: ${color('gray100')};
    border-color: ${color('gray100')};
  `)}

  ${ifProp('hasFocus', css`
    border-color: ${color('primary')};
  `)}

  ${ifProp('hasError', css`
    border-color: ${color('danger')};
  `)}

  &:focus-visible {
    border-color: ${color('gray400')};
    outline: 2px solid ${color('blue200')};
  }

  &:hover {
    ${ifProp({ disabled: false, hasError: false, hasFocus: false }, css`
      border-color: ${color('gray400')};
    `)}
  }
`;

/**
 * Export `textDesktopStyle`.
 */

export const textDesktopStyle = css`
  ${typography.styles.textNormal};

  font-size: 16px;
  line-height: 22px;
`;

/**
 * Text mobile style.
 */

const textMobileStyle = css`
  ${typography.styles.textExtraSmall};

  font-size: 12px;
  line-height: 16px;
`;

/**
 * Export `Label` styled component.
 */

export const Label = styled.label<{ disabled: boolean; isFloating?: boolean }>`
  ${textDesktopStyle}

  color: ${color('gray500')};
  left: 10px;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  right: 10px;
  text-overflow: ellipsis;
  top: 13px;
  transform: translateY(0);
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color, transform;
  white-space: nowrap;

  ${ifProp('isFloating', css`
    ${textMobileStyle}

    transform: translateY(-8px);
  `)}

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}

  ${media.min('md')`
    left: 14px;
    right: 14px;
    top: 14px;

    ${ifProp('isFloating', css`
      transform: translateY(-10px);
    `)}
  `}
`;

/**
 * Export `FormControl` styled component.
 */

export const FormControl = styled.input<{ hasError?: boolean }>`
  ${textDesktopStyle}

  background: none;
  border: 0;
  box-shadow: none;
  color: ${color('secondary')};
  display: block;
  padding: 20px 10px 6px;
  transition: color ${theme('animations.defaultTransition')};
  width: 100%;

  ${ifProp('hasError', css`
    color: ${color('danger')};
  `)}

  ${ifProp({ as: 'textarea' }, css`
    height: auto;
    resize: vertical;
  `)}

  ${media.min('md')`
    padding-left: 14px;
    padding-right: 14px;
    padding-top: 22px;
  `}

  &:disabled {
    color: ${color('gray400')};
  }
`;

/**
 * Export `MaxLength` styled component.
 */

export const MaxLength = styled(Text).attrs({ as: 'span', size: 'normal' })<{ disabled: boolean }>`
  color: ${color('gray500')};
  padding: 20px 10px 6px 0;
  position: relative;
  transition: color ${theme('animations.defaultTransition')};

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}

  ${media.min('md')`
    padding-left: 4px;
    padding-right: 14px;
    padding-top: 22px;
  `}
`;
